import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, Copy, Check, ExternalLink, Globe, Hash, Linkedin, Mail, MapPin, Pencil, Phone, User } from 'lucide-react';
import { fmtPhone, linkedinLabel } from './crmApi';

const telHref = (phone, ext) => {
  const d = String(phone || '').replace(/\D/g, '');
  if (!d) return null;
  return `tel:${d.length === 10 ? `+1${d}` : `+${d}`}${ext ? `;ext=${ext}` : ''}`;
};
const webHref = (w) => (/^https?:/i.test(w) ? w : `https://${w}`);

const Ext = ({ href, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-700 hover:underline break-all" data-testid={testid}>
    {children}<ExternalLink className="h-3 w-3 shrink-0" />
  </a>
);

// CRM-FU-WORKSPACE-001: one-click copy for phone / email
const CopyBtn = ({ value, testid }) => {
  const [ok, setOk] = React.useState(false);
  const copy = async (e) => {
    e.preventDefault();
    try { await navigator.clipboard.writeText(String(value)); setOk(true); setTimeout(() => setOk(false), 1200); } catch { /* clipboard unavailable */ }
  };
  return (
    <button type="button" onClick={copy} title="Copy" className="ml-1.5 inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-violet-700 hover:bg-violet-50 align-middle" data-testid={testid} data-copied={ok ? 'true' : 'false'}>
      {ok ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

const Item = ({ icon: Icon, label, testid, children }) => (
  <div className="min-w-0" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
    <div className="text-sm text-slate-900 font-medium break-words">{children}</div>
  </div>
);

// CRM-CONTACT-001: compact, data-driven contact block — only fields with data render (never "—"/"N/A").
export const LeadContactDetails = ({ lead, onEdit, columns = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4', title = 'Contact Details', extra = null }) => {
  const c = lead.contact || {}; const co = lead.company || {};
  const contactName = c.name || (lead.contact_name && lead.contact_name !== '—' ? lead.contact_name : '');
  const location = [co.city, co.state, co.country].filter(Boolean).join(', ');
  const items = [
    contactName && <Item key="name" icon={User} label="Contact" testid="lcd-contact-name">{contactName}</Item>,
    c.job_title && <Item key="title" icon={Briefcase} label="Job Title" testid="lcd-job-title">{c.job_title}</Item>,
    c.phone && <Item key="phone" icon={Phone} label="Phone" testid="lcd-phone"><a href={telHref(c.phone, c.phone_ext)} className="text-violet-700 hover:underline" data-testid="lcd-phone-link">{fmtPhone(c.phone)}</a><CopyBtn value={c.phone_ext ? `${c.phone} ext. ${c.phone_ext}` : c.phone} testid="lcd-phone-copy" /></Item>,
    c.phone && c.phone_ext && <Item key="ext" icon={Hash} label="Extension" testid="lcd-ext">{c.phone_ext}</Item>,
    c.email && <Item key="email" icon={Mail} label="Email" testid="lcd-email"><a href={`mailto:${c.email}`} className="text-violet-700 hover:underline" data-testid="lcd-email-link">{c.email}</a><CopyBtn value={c.email} testid="lcd-email-copy" /></Item>,
    c.linkedin && <Item key="li" icon={Linkedin} label="LinkedIn" testid="lcd-linkedin"><Ext href={c.linkedin} testid="lcd-linkedin-link">{linkedinLabel(c.linkedin)}</Ext></Item>,
    (co.name || lead.company_name) && <Item key="co" icon={Building2} label="Company" testid="lcd-company"><Link to={`/crm/companies/${lead.company_id}`} className="text-violet-700 hover:underline" data-testid="lcd-company-link">{co.name || lead.company_name}</Link></Item>,
    co.website && <Item key="web" icon={Globe} label="Website" testid="lcd-website"><Ext href={webHref(co.website)} testid="lcd-website-link">{co.website}</Ext></Item>,
    !c.linkedin && co.linkedin && <Item key="coli" icon={Linkedin} label="Company LinkedIn" testid="lcd-company-linkedin"><Ext href={co.linkedin} testid="lcd-company-linkedin-link">{linkedinLabel(co.linkedin)}</Ext></Item>,
    location && <Item key="loc" icon={MapPin} label="Location" testid="lcd-location">{location}</Item>,
  ].filter(Boolean);
  const reachable = Boolean(c.phone || c.email || c.linkedin);
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="lead-contact-details">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-violet-600" /> {title}</div>
        <div className="flex items-center gap-2">
          {!reachable && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="lcd-no-reach">No phone, email or LinkedIn on file</span>}
          {onEdit && <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-xs text-violet-700 hover:underline" data-testid="lcd-edit"><Pencil className="h-3 w-3" /> Edit</button>}
        </div>
      </div>
      <div className={`grid ${columns} gap-x-6 gap-y-3`}>{items}{extra}</div>
    </section>
  );
};
